import { z } from "zod";

/**
 * Agent tools (openspec change `agent-tools-platform`, ADR 0011). A tool is declared once in
 * core; providers implement it. Every outbound call (one that sends arguments outside ESI)
 * passes one egress gate, may wait for the user's confirmation, and appears in the message as
 * an activity row. These schemas are what clients read and send.
 */

/** A tool's ID: the suffix of its settings key `tools.<toolId>`, e.g. `webSearch`. */
export const toolIdSchema = z.string().regex(/^[a-z][A-Za-z0-9]{0,39}$/);
export type ToolId = z.infer<typeof toolIdSchema>;

/** A provider's ID, e.g. `agentcore`: what `tools.<toolId>.provider` names. */
export const toolProviderIdSchema = z.string().regex(/^[a-z][a-z0-9-]{0,39}$/);
export type ToolProviderId = z.infer<typeof toolProviderIdSchema>;

/** `internal`: reads only inside ESI. `outbound`: sends its arguments outside ESI. */
export const TOOL_EGRESS_CLASSES = ["internal", "outbound"] as const;
export type ToolEgressClass = (typeof TOOL_EGRESS_CLASSES)[number];

/** What the gate decides for an outbound call; `block` beats `confirm` beats `allow`. */
export const EGRESS_ACTIONS = ["allow", "confirm", "block"] as const;
export const egressActionSchema = z.enum(EGRESS_ACTIONS);
export type EgressAction = z.infer<typeof egressActionSchema>;

/**
 * Why the gate did not simply allow a call. Never names a document or a passage.
 * - `classification`: the policy for a readers floor of the turn's label (or a floor that
 *   could not be resolved, which counts as `named`);
 * - `verbatim` / `verbatim_confidential`: a run of 40 or more characters copied from a passage
 *   read in the turn (of an `entity`- or `named`-floor document for the latter);
 * - `handle`: a citation or document handle (`S#`, `D#`, `W#`) in the arguments;
 * - `residency`: the provider's geography is not allowed by the turn's residency;
 * - `turn_limit`: the turn already made its outbound calls;
 * - `model_budget`: the tenant's hard monthly spend limit is reached (spec `tenant-plans`);
 * - `user_budget`: the turn reached what is left of the user's monthly share of that limit
 *   (`models.userMonthlySharePct`);
 * - `no_confirmation_channel`: the gate asked for confirmation in a turn that has no way to ask
 *   the user (an MCP turn): blocked instead;
 * - `url_provenance`: a URL the conversation never saw (`web_fetch`): the agent built it;
 * - `fetch_policy`: `tools.webFetch.approval` is `always`;
 * - `hidden_characters`: the arguments hold characters the user cannot see (blocked before the
 *   gate, never asked about).
 */
export const EGRESS_REASONS = [
  "classification",
  "verbatim",
  "verbatim_confidential",
  "handle",
  "residency",
  "turn_limit",
  "model_budget",
  "user_budget",
  "no_confirmation_channel",
  "url_provenance",
  "fetch_policy",
  "hidden_characters",
] as const;
export const egressReasonSchema = z.enum(EGRESS_REASONS);
export type EgressReason = z.infer<typeof egressReasonSchema>;

/** The gate's outcome: the strictest action of all checks, with every reason that applied. */
export const egressDecisionSchema = z.object({
  decision: egressActionSchema,
  reasons: z.array(egressReasonSchema),
});
export type EgressDecision = z.infer<typeof egressDecisionSchema>;

/** `tools.egress`: the action per classification readers floor, never per level ID. */
export const egressPolicySchema = z.strictObject({
  project: egressActionSchema,
  entity: egressActionSchema,
  named: egressActionSchema,
});
export type EgressPolicy = z.infer<typeof egressPolicySchema>;

/** At most this many outbound calls per turn, across all tools. */
export const MAX_OUTBOUND_CALLS_PER_TURN = 8;

/** A pending approval expires after this long. */
export const TOOL_APPROVAL_TTL_MS = 15 * 60 * 1000;

/**
 * `POST …/conversations/:conversationId/tool-approvals/:approvalId`: the user's decision on a
 * pending outbound call. The arguments are never sent back: the server approves the ones it
 * stored, bound by their hash.
 */
export const toolApprovalDecisionSchema = z.strictObject({
  decision: z.enum(["approve", "deny"]),
});
export type ToolApprovalDecision = z.infer<typeof toolApprovalDecisionSchema>;

/** The state of an approval as a viewer sees it. */
export const TOOL_APPROVAL_STATES = ["pending", "approved", "denied", "expired"] as const;
export type ToolApprovalState = (typeof TOOL_APPROVAL_STATES)[number];

/**
 * The `data-tool-approvals` part of an assistant message: the state of each approval it asked
 * for, by approval ID, rebuilt from the server's rows for every render (like citations).
 */
export const TOOL_APPROVALS_PART = "data-tool-approvals";
export const toolApprovalsDataSchema = z.record(
  z.string(),
  z.object({
    state: z.enum(TOOL_APPROVAL_STATES),
    expiresAt: z.iso.datetime(),
    /**
     * Why the gate asked (reason codes, e.g. `url_provenance`): the card explains it. Any string,
     * as for refusals: a client shows only the codes it knows.
     */
    reasons: z.array(z.string()).optional(),
    /** The names of the files a `run_code` call copies in (agent-turn D18): the card shows them. */
    files: z.array(z.string()).optional(),
  }),
);
export type ToolApprovalsData = z.infer<typeof toolApprovalsDataSchema>;

/**
 * What an outbound tool returns to the turn when it did not run. `blocked`: refused by the gate
 * (with its reasons) or for invisible characters (`hidden_characters`); `failed`: the provider
 * failed; `refused`: the arguments exceed the tool's declared limits. The model reads `error`;
 * the activity row reads `status`.
 */
export const toolRefusalSchema = z.object({
  status: z.enum(["blocked", "failed", "refused"]),
  // Any string: a client older than the server still reads a refusal with a newer reason
  // (version skew), and shows only the codes it knows (`refusalOf`).
  reasons: z.array(z.string()).optional(),
  /**
   * For `failed`: a closed code of why (`timeout`, a provider code or `failed`), read by a
   * feedback draft's agent context (feedback-from-conversation D4). Absent from older turns.
   */
  reason: z.string().optional(),
  error: z.string(),
});
export type ToolRefusal = z.infer<typeof toolRefusalSchema>;

/** How an activity row ends. `pending`: waiting for the user's decision. */
export const TOOL_ACTIVITY_OUTCOMES = [
  "running",
  "pending",
  "done",
  "blocked",
  "declined",
  "expired",
  "failed",
] as const;
export type ToolActivityOutcome = (typeof TOOL_ACTIVITY_OUTCOMES)[number];

/** A tool part of a UI message, as far as the activity row reads it (AI SDK `ToolUIPart`). */
interface ToolPartLike {
  type: string;
  state?: unknown;
  output?: unknown;
  approval?: { id?: unknown } | undefined;
}

export const isToolPart = (part: unknown): part is ToolPartLike =>
  typeof part === "object" &&
  part !== null &&
  typeof (part as { type?: unknown }).type === "string" &&
  ((part as { type: string }).type.startsWith("tool-") ||
    (part as { type: string }).type === "dynamic-tool");

/** The model-facing tool name of a tool part (`tool-web_search` → `web_search`). */
export function toolNameOf(part: { type: string; toolName?: unknown }): string {
  if (part.type === "dynamic-tool") {
    return typeof part.toolName === "string" ? part.toolName : "";
  }
  return part.type.slice("tool-".length);
}

/**
 * The outcome of a tool part for its activity row. `approvals` (the message's
 * `data-tool-approvals`) turns a pending request whose approval expired into `expired`.
 */
export function toolActivityOutcome(
  part: ToolPartLike,
  approvals?: ToolApprovalsData,
): ToolActivityOutcome {
  switch (part.state) {
    case "approval-requested": {
      const id = typeof part.approval?.id === "string" ? part.approval.id : undefined;
      const state = id ? approvals?.[id]?.state : undefined;
      if (state === "expired") {
        return "expired";
      }
      if (state === "denied") {
        return "declined";
      }
      // Approved, yet the part never ran: its continuation failed.
      return state === "approved" ? "failed" : "pending";
    }
    case "approval-responded":
      return "running";
    case "output-denied":
      return "declined";
    case "output-error":
      return "failed";
    case "output-available": {
      const refusal = toolRefusalSchema.safeParse(part.output);
      if (!refusal.success) {
        return "done";
      }
      return refusal.data.status === "blocked" ? "blocked" : "failed";
    }
    default:
      return "running";
  }
}

// ── Web search settings (openspec change `web-search`) ──────────────────────────────────

/** Domains per list of `tools.webSearch.domains` at most (the provider's own limit). */
export const MAX_WEB_SEARCH_DOMAINS = 100;

const HOSTNAME =
  /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * A domain as admins write it, normalised: a host name without scheme, port, path or wildcard,
 * lower-cased and IDNA-encoded (`bücher.example` → `xn--bcher-kva.example`). A root domain
 * covers its subdomains. Anything else is refused, naming the entry.
 */
export const webDomainSchema = z.string().transform((value, ctx) => {
  const candidate = value.trim().toLowerCase();
  let hostname: string | undefined;
  if (candidate !== "" && !/[\s/:?#@\\*%[\]]/.test(candidate)) {
    try {
      hostname = new URL(`https://${candidate}`).hostname;
    } catch {
      hostname = undefined;
    }
  }
  if (!hostname || !HOSTNAME.test(hostname)) {
    ctx.addIssue({
      code: "custom",
      message: `Invalid domain "${value.slice(0, 260)}": write a host name such as example.com`,
    });
    return z.NEVER;
  }
  return hostname;
});

/** Whether `domain` is `parent` or one of its subdomains. */
export function isWithinDomain(domain: string, parent: string): boolean {
  return domain === parent || domain.endsWith(`.${parent}`);
}

/**
 * Whether `host` passes allow and deny lists: in no `exclude` domain and, when `include` is set,
 * in one of its domains. A trailing dot is ignored (`example.com.` is `example.com`).
 */
export function withinDomainLists(
  host: string,
  domains: { readonly include: readonly string[]; readonly exclude: readonly string[] },
): boolean {
  const name = host.toLowerCase().replace(/\.$/, "");
  return (
    !domains.exclude.some((d) => isWithinDomain(name, d)) &&
    (domains.include.length === 0 || domains.include.some((d) => isWithinDomain(name, d)))
  );
}

/**
 * `tools.webSearch.domains` and `tools.webFetch.domains`: allow and deny lists, each of distinct
 * normalised domains.
 */
export const webDomainsSchema = z.strictObject({
  include: z
    .array(webDomainSchema)
    .max(MAX_WEB_SEARCH_DOMAINS)
    .transform((list) => [...new Set(list)]),
  exclude: z
    .array(webDomainSchema)
    .max(MAX_WEB_SEARCH_DOMAINS)
    .transform((list) => [...new Set(list)]),
});
export type WebDomains = z.output<typeof webDomainsSchema>;
/** The web search name of {@link webDomainsSchema}. */
export const webSearchDomainsSchema = webDomainsSchema;
export type WebSearchDomains = WebDomains;

/**
 * Whether `value` is at least as strict as `inherited`: it excludes everything `inherited`
 * excludes (directly or through a parent domain) and, when `inherited` limits results to some
 * domains, it limits them to domains within those.
 */
export function webDomainsNarrow(value: WebDomains, inherited: WebDomains) {
  // An inherited exclusion holds when the value excludes it too, or when the value's own
  // allow list cannot reach it (no allowed domain is within it, nor contains it).
  const excludes = inherited.exclude.every(
    (e) =>
      value.exclude.some((x) => isWithinDomain(e, x)) ||
      (value.include.length > 0 &&
        value.include.every((i) => !isWithinDomain(i, e) && !isWithinDomain(e, i))),
  );
  if (!excludes) {
    return false;
  }
  if (inherited.include.length === 0) {
    return true;
  }
  return (
    value.include.length > 0 &&
    value.include.every((i) => inherited.include.some((j) => isWithinDomain(i, j)))
  );
}

/** The web search name of {@link webDomainsNarrow}. */
export const webSearchDomainsNarrow = webDomainsNarrow;

/** `tools.webSearch.maxAgeDays`: the oldest published date allowed, in days; null: no limit. */
export const webSearchMaxAgeDaysSchema = z.int().min(1).max(3650).nullable();

// ── Usage (spec `usage-reporting` "Tool usage is counted without content") ─────────────────

/** How the gate and the user decided a call that was metered. */
export const TOOL_USAGE_DECISIONS = ["allow", "confirm", "block", "declined"] as const;
export const toolUsageDecisionSchema = z.enum(TOOL_USAGE_DECISIONS);
export type ToolUsageDecision = z.infer<typeof toolUsageDecisionSchema>;

/** `not_sent`: nothing left ESI (blocked, declined or refused before the provider). */
export const TOOL_CALL_OUTCOMES = ["ok", "error", "not_sent"] as const;
export const toolCallOutcomeSchema = z.enum(TOOL_CALL_OUTCOMES);
export type ToolCallOutcome = z.infer<typeof toolCallOutcomeSchema>;

/** Tool figures of a report row: calls and estimated cost (null when unknown). */
export const toolUsageFiguresSchema = z.object({
  tool: toolIdSchema,
  calls: z.int().min(0),
  blocked: z.int().min(0),
  declined: z.int().min(0),
  costUsd: z.number().min(0),
  /** Calls whose cost is unknown: not in `costUsd`. */
  unknownCostCalls: z.int().min(0),
});
export type ToolUsageFigures = z.infer<typeof toolUsageFiguresSchema>;

/**
 * The stage's deployed tool providers (`ESI_TOOL_PROVIDERS`), e.g.
 * `webSearch=agentcore;codeExecution=agentcore,other`. Unset or empty: none (no tool is
 * offered). A malformed entry throws at startup rather than silently offering nothing.
 */
export function toolProvidersFromEnv(
  env: Record<string, string | undefined>,
): Readonly<Record<ToolId, readonly ToolProviderId[]>> {
  const raw = env.ESI_TOOL_PROVIDERS?.trim();
  const providers: Record<ToolId, ToolProviderId[]> = {};
  if (!raw) {
    return providers;
  }
  for (const entry of raw.split(";").map((e) => e.trim())) {
    if (!entry) {
      continue;
    }
    const [tool, list, ...rest] = entry.split("=").map((v) => v.trim());
    const ids = (list ?? "").split(",").map((v) => v.trim());
    if (
      rest.length > 0 ||
      !toolIdSchema.safeParse(tool).success ||
      ids.some((id) => !toolProviderIdSchema.safeParse(id).success)
    ) {
      throw new Error(`ESI_TOOL_PROVIDERS: malformed entry "${entry}"`);
    }
    providers[tool as ToolId] = [...new Set(ids)];
  }
  return providers;
}
